/**
 * Alert - Alert/notification banner component with Pod Ops styling
 */

import type React from 'react';
import { useState } from 'react'
import { theme } from '../../theme'
import { Info, AlertCircle, CheckCircle, AlertTriangle, X } from 'lucide-react'

export interface AlertProps {
  message?: string
  description?: React.ReactNode
  type?: 'info' | 'success' | 'warning' | 'error'
  showIcon?: boolean
  closable?: boolean
  onClose?: () => void
  style?: React.CSSProperties
}

export const Alert: React.FC<AlertProps> = ({
  message,
  description,
  type = 'info',
  showIcon = false,
  closable = false,
  onClose,
  style,
}) => {
  const [visible, setVisible] = useState(true)

  if (!visible) return null

  const handleClose = () => {
    setVisible(false)
    onClose?.()
  }

  // Get colors based on type
  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          bg: '#f6ffed',
          border: theme.colors.success,
          text: '#135200',
          icon: theme.colors.success,
        }
      case 'warning':
        return {
          bg: '#fffbe6',
          border: theme.colors.warning,
          text: '#7c4a03',
          icon: theme.colors.warning,
        }
      case 'error':
        return {
          bg: '#fff2f0',
          border: theme.colors.error,
          text: '#a8071a',
          icon: theme.colors.error,
        }
      case 'info':
      default:
        return {
          bg: theme.colors.primaryLight,
          border: theme.colors.primary,
          text: theme.colors.primary,
          icon: theme.colors.primary,
        }
    }
  }

  const colors = getColors()

  // Get icon based on type
  const getIcon = () => {
    const iconSize = 20
    switch (type) {
      case 'success':
        return <CheckCircle size={iconSize} />
      case 'warning':
        return <AlertTriangle size={iconSize} />
      case 'error':
        return <AlertCircle size={iconSize} />
      case 'info':
      default:
        return <Info size={iconSize} />
    }
  }

  // Styles
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing.md,
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: theme.borderRadius.md,
    ...style,
  }

  const iconWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing.sm,
    color: colors.icon,
    flexShrink: 0,
  }

  const contentStyle: React.CSSProperties = {
    flex: 1,
  }

  const messageStyle: React.CSSProperties = {
    ...theme.typography.bodyBold,
    color: colors.text,
    marginBottom: description ? theme.spacing.xs : 0,
  }

  const descriptionStyle: React.CSSProperties = {
    ...theme.typography.body,
    color: colors.text,
  }

  const closeButtonStyle: React.CSSProperties = {
    marginLeft: theme.spacing.sm,
    padding: theme.spacing.xs,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: colors.text,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    opacity: 0.6,
    transition: `opacity ${theme.animations.duration.fast}ms ${theme.animations.easing.easeInOut}`,
  }

  return (
    <div style={containerStyle}>
      {showIcon && <div style={iconWrapperStyle}>{getIcon()}</div>}

      <div style={contentStyle}>
        {message && <div style={messageStyle}>{message}</div>}
        {description && <div style={descriptionStyle}>{description}</div>}
      </div>

      {closable && (
        <button
          style={closeButtonStyle}
          onClick={handleClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.6'
          }}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      )}
    </div>
  )
}
